import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Button } from '@ci-ville/ui';
const ConfirmContext = React.createContext(null);
/**
 * Confirmation des actions sur un signalement (statut, assignation, message…) :
 * chaque modification passe par « Valider ? Oui / Non » avant d'être envoyée.
 */
export function ConfirmProvider({ children }) {
    const [options, setOptions] = React.useState(null);
    const [reason, setReason] = React.useState('');
    const resolver = React.useRef(null);
    const confirmRef = React.useRef(null);
    const reasonRef = React.useRef(null);
    const confirm = React.useCallback((opts) => {
        resolver.current?.(null);
        setReason(opts.reason?.defaultValue ?? '');
        setOptions(opts);
        return new Promise((resolve) => { resolver.current = resolve; });
    }, []);
    const close = React.useCallback((result) => {
        resolver.current?.(result);
        resolver.current = null;
        setOptions(null);
    }, []);
    const minLength = options?.reason?.minLength ?? 1;
    const canConfirm = !options?.reason || reason.trim().length >= minLength;
    const accept = () => {
        if (!canConfirm)
            return;
        close(options?.reason ? { reason: reason.trim() } : {});
    };
    React.useEffect(() => {
        if (!options)
            return;
        (options.reason ? reasonRef.current : confirmRef.current)?.focus();
        const onKey = (e) => { if (e.key === 'Escape')
            close(null); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [options, close]);
    return (_jsxs(ConfirmContext.Provider, { value: confirm, children: [children, options && (_jsx("div", { className: "fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 p-4", onClick: () => close(null), role: "alertdialog", "aria-modal": "true", "aria-labelledby": "confirm-title", children: _jsxs("div", { className: "w-full max-w-md rounded-none border border-border bg-card shadow-2xl", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "space-y-2 p-5", children: [_jsx("h2", { id: "confirm-title", className: "text-base font-semibold", children: options.title }), options.message && _jsx("div", { className: "text-sm text-muted-foreground", children: options.message }), options.reason && (_jsxs("label", { className: "block pt-1 text-sm", children: [_jsx("span", { className: "mb-1 block font-medium", children: options.reason.label }), _jsx("textarea", { ref: reasonRef, value: reason, onChange: (e) => setReason(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey))
                                                accept(); }, placeholder: options.reason.placeholder, rows: 3, maxLength: 2000, className: "w-full resize-none rounded-none border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" })] }))] }), _jsxs("div", { className: "flex justify-end gap-2 border-t border-border p-3", children: [_jsx(Button, { variant: "outline", size: "sm", className: "rounded-none", onClick: () => close(null), children: options.cancelLabel ?? 'Non' }), _jsx(Button, { ref: confirmRef, variant: options.tone === 'danger' ? 'destructive' : 'brand', size: "sm", className: "rounded-none", disabled: !canConfirm, onClick: accept, children: options.confirmLabel ?? 'Oui' })] })] }) }))] }));
}
export function useConfirm() {
    const ctx = React.useContext(ConfirmContext);
    if (!ctx)
        throw new Error('useConfirm doit être utilisé dans ConfirmProvider');
    return ctx;
}
//# sourceMappingURL=ConfirmContext.js.map